import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Pencil, ChevronDown, ChevronUp } from 'lucide-react';
import api from '@/services/apiClient';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Textarea from '@/components/ui/Textarea';
import Badge from '@/components/ui/Badge';
import EmptyState from '@/components/ui/EmptyState';
import Spinner from '@/components/ui/Spinner';
import { fmtDate, fmtNum } from '@/lib/enums';
import { POLLOCK7_FOLDS, FOLD_LABELS, previewBodyFat, ageFromBirthdate } from '@/lib/protocols';

const PROTOCOLS = ['pollock7', 'weltman', 'manual'];
const CIRCUMFERENCES = ['cintura', 'peito', 'braco', 'quadril', 'coxa', 'panturrilha'];

const emptyForm = () => ({
  protocol: 'pollock7',
  date: new Date().toISOString().slice(0, 10),
  weightKg: '',
  folds: {},
  abdomen1Cm: '',
  abdomen2Cm: '',
  bodyFatPct: '',
  circumferences: {},
  notes: '',
});

const formFromAssessment = (a) => ({
  protocol: a.protocol,
  date: a.date,
  weightKg: a.weightKg != null ? Number(a.weightKg) : '',
  folds: a.inputs?.folds || {},
  abdomen1Cm: a.inputs?.abdomen1Cm ?? '',
  abdomen2Cm: a.inputs?.abdomen2Cm ?? '',
  bodyFatPct: a.inputs?.bodyFatPct ?? '',
  circumferences: a.inputs?.circumferences || {},
  notes: a.notes || '',
});

const numOrNull = (v) => (v === '' || v == null ? null : Number(v));
const recordOf = (obj) => {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) if (v !== '' && v != null) out[k] = Number(v);
  return Object.keys(out).length ? out : null;
};

/** Avaliações físicas do aluno: lista com deltas + formulário por protocolo. */
export default function AssessmentsSection({ userId, profile }) {
  const { t } = useTranslation();
  const [assessments, setAssessments] = useState(null);
  const [editing, setEditing] = useState(null); // null | { id? , form }
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const load = () =>
    api
      .get('/evolution/assessments', { userId })
      .then((res) => setAssessments((res.data || res).assessments || []))
      .catch(() => setAssessments([]));

  useEffect(() => {
    setAssessments(null);
    setEditing(null);
    if (userId) load();
  }, [userId]);

  const form = editing?.form;
  const setField = (k, v) => setEditing((e) => ({ ...e, form: { ...e.form, [k]: v } }));
  const setNested = (group, k, v) =>
    setEditing((e) => ({ ...e, form: { ...e.form, [group]: { ...e.form[group], [k]: v } } }));

  const preview = useMemo(() => {
    if (!form) return null;
    return previewBodyFat({
      protocol: form.protocol,
      sex: profile?.sex || null,
      ageYears: ageFromBirthdate(profile?.birthdate),
      heightCm: profile?.heightCm || null,
      weightKg: form.weightKg,
      inputs: {
        folds: form.folds,
        abdomen1Cm: form.abdomen1Cm,
        abdomen2Cm: form.abdomen2Cm,
        bodyFatPct: form.bodyFatPct,
      },
    });
  }, [form, profile]);

  const missingSex = !profile?.sex && form && form.protocol !== 'manual';

  const save = async () => {
    setSaving(true);
    setErr(null);
    const body = {
      userId,
      date: form.date,
      protocol: form.protocol,
      weightKg: numOrNull(form.weightKg),
      inputs: {
        folds: form.protocol === 'pollock7' ? recordOf(form.folds) : null,
        abdomen1Cm: form.protocol === 'weltman' ? numOrNull(form.abdomen1Cm) : null,
        abdomen2Cm: form.protocol === 'weltman' ? numOrNull(form.abdomen2Cm) : null,
        bodyFatPct: form.protocol === 'manual' ? numOrNull(form.bodyFatPct) : null,
        circumferences: recordOf(form.circumferences),
      },
      notes: form.notes || null,
    };
    try {
      if (editing.id) await api.put(`/evolution/assessments/${editing.id}`, body);
      else await api.post('/evolution/assessments', body);
      setEditing(null);
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('students.assessments.confirmDelete', { date: fmtDate(a.date) }))) return;
    await api.delete(`/evolution/assessments/${a.id}`).catch(() => {});
    await load();
  };

  if (assessments === null) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}><Spinner /></div>;
  }

  const deltaOf = (i, key) => {
    const prev = assessments[i + 1];
    const cur = assessments[i];
    const a = cur?.results?.[key];
    const b = prev?.results?.[key];
    if (a == null || b == null) return null;
    return Math.round((a - b) * 10) / 10;
  };

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="kicker">{t('students.assessments.title')}</div>
        {!editing && (
          <Button size="sm" onClick={() => setEditing({ form: emptyForm() })}>
            <Plus size={14} /> {t('students.assessments.new')}
          </Button>
        )}
      </div>

      {editing && (
        <div style={{ background: 'var(--surf)', border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', padding: 16, display: 'grid', gap: 'var(--space-3)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 'var(--space-3)' }}>
            <Select
              label={t('students.assessments.protocol')}
              value={form.protocol}
              onChange={(e) => setField('protocol', e.target.value)}
              options={PROTOCOLS.map((p) => ({ value: p, label: t(`students.assessments.protocols.${p}`) }))}
            />
            <Input label={t('students.assessments.date')} type="date" value={form.date} onChange={(e) => setField('date', e.target.value)} />
            <Input label={t('students.assessments.weight')} type="number" value={form.weightKg} onChange={(e) => setField('weightKg', e.target.value)} />
          </div>

          {missingSex && (
            <div style={{ color: 'var(--warning)', fontSize: 13 }}>{t('students.assessments.sexMissing')}</div>
          )}

          {form.protocol === 'pollock7' && (
            <div>
              <div className="kicker" style={{ marginBottom: 8 }}>{t('students.assessments.folds')}</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-3)' }}>
                {POLLOCK7_FOLDS.map((k) => (
                  <Input key={k} label={`${FOLD_LABELS[k]} (mm)`} type="number" value={form.folds[k] ?? ''} onChange={(e) => setNested('folds', k, e.target.value)} />
                ))}
              </div>
            </div>
          )}

          {form.protocol === 'weltman' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
              <Input label={t('students.assessments.abdomen1')} type="number" value={form.abdomen1Cm} onChange={(e) => setField('abdomen1Cm', e.target.value)} />
              <Input label={t('students.assessments.abdomen2')} type="number" value={form.abdomen2Cm} onChange={(e) => setField('abdomen2Cm', e.target.value)} />
            </div>
          )}

          {form.protocol === 'manual' && (
            <Input label={t('students.assessments.bodyFatManual')} type="number" value={form.bodyFatPct} onChange={(e) => setField('bodyFatPct', e.target.value)} />
          )}

          <div>
            <div className="kicker" style={{ marginBottom: 8 }}>{t('students.assessments.circumferences')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 'var(--space-3)' }}>
              {CIRCUMFERENCES.map((k) => (
                <Input key={k} label={`${t(`students.assessments.circ.${k}`)} (cm)`} type="number" value={form.circumferences[k] ?? ''} onChange={(e) => setNested('circumferences', k, e.target.value)} />
              ))}
            </div>
          </div>

          <Textarea label={t('students.assessments.notes')} rows={2} value={form.notes} onChange={(e) => setField('notes', e.target.value)} />

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--frost)' }}>
              {preview != null ? t('students.assessments.preview', { pct: preview }) : t('students.assessments.previewPending')}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="ghost" onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
              <Button loading={saving} onClick={save}>{t('common.save')}</Button>
            </div>
          </div>
          {err && <span style={{ color: 'var(--danger)', fontSize: 13 }}>{err}</span>}
        </div>
      )}

      {assessments.length === 0 && !editing ? (
        <EmptyState title={t('students.assessments.emptyTitle')} description={t('students.assessments.emptyDesc')} />
      ) : (
        assessments.map((a, i) => {
          const dFat = deltaOf(i, 'bodyFatPct');
          const dLean = deltaOf(i, 'leanMassKg');
          const open = expanded === a.id;
          return (
            <div key={a.id} style={{ background: 'var(--surf)', border: '1px solid var(--line)', borderRadius: 'var(--radius-md)', padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <strong style={{ color: 'var(--hi)' }}>{fmtDate(a.date)}</strong>
                    <Badge variant="accent">{t(`students.assessments.protocols.${a.protocol}`)}</Badge>
                    {a.assessor?.name && <span style={{ color: 'var(--low)', fontSize: 12 }}>{a.assessor.name}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 13, color: 'var(--mid)' }}>
                    <span>%G <strong style={{ color: 'var(--volt)' }}>{fmtNum(a.results?.bodyFatPct)}%</strong>{dFat != null && <em style={{ fontStyle: 'normal', color: dFat <= 0 ? 'var(--volt)' : 'var(--warning)' }}> ({dFat > 0 ? '+' : ''}{dFat})</em>}</span>
                    {a.results?.leanMassKg != null && <span>{t('students.assessments.leanMass')} <strong style={{ color: 'var(--hi)' }}>{fmtNum(a.results.leanMassKg)}kg</strong>{dLean != null && <em style={{ fontStyle: 'normal', color: dLean >= 0 ? 'var(--volt)' : 'var(--warning)' }}> ({dLean > 0 ? '+' : ''}{dLean})</em>}</span>}
                    {a.weightKg != null && <span>{t('students.assessments.weightShort')} <strong style={{ color: 'var(--hi)' }}>{fmtNum(a.weightKg)}kg</strong></span>}
                  </div>
                </div>
                <button type="button" onClick={() => setExpanded(open ? null : a.id)} style={{ background: 'none', border: 'none', color: 'var(--mid)', padding: 4 }}>
                  {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>
                <button type="button" onClick={() => setEditing({ id: a.id, form: formFromAssessment(a) })} style={{ background: 'none', border: 'none', color: 'var(--mid)', padding: 4 }}>
                  <Pencil size={15} />
                </button>
                <button type="button" onClick={() => remove(a)} style={{ background: 'none', border: 'none', color: 'var(--low)', padding: 4 }}>
                  <Trash2 size={15} />
                </button>
              </div>
              {open && (
                <div style={{ marginTop: 10, display: 'grid', gap: 6, fontSize: 13, color: 'var(--mid)' }}>
                  {a.inputs?.folds && (
                    <div>{POLLOCK7_FOLDS.filter((k) => a.inputs.folds[k] != null).map((k) => `${FOLD_LABELS[k]}: ${a.inputs.folds[k]}mm`).join(' · ')}</div>
                  )}
                  {a.inputs?.abdomen1Cm && <div>{t('students.assessments.abdomenAvg')}: {fmtNum((Number(a.inputs.abdomen1Cm) + Number(a.inputs.abdomen2Cm)) / 2)}cm</div>}
                  {a.inputs?.circumferences && (
                    <div>{Object.entries(a.inputs.circumferences).map(([k, v]) => `${t(`students.assessments.circ.${k}`, k)}: ${v}cm`).join(' · ')}</div>
                  )}
                  {a.results?.bodyDensity != null && <div>{t('students.assessments.density')}: {a.results.bodyDensity}</div>}
                  {a.notes && <div>{a.notes}</div>}
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
